/**
 * Rental Status wrapper card (design §8 / fullscreen-spec §14). v1's blocks
 * are DATA-PRESENCE driven, not status-switched (data-map §11, user override
 * of parity §14 gap 2) — preserved here, shared by BOTH screening kinds:
 * - Currently Renting card when currentAddress set (v1:6379-6389); hosts
 *   N4 / L1 when stored (legacy — the form no longer asks) + New To Country
 *   when strictly 'yes' (v1:6429-6435).
 * - Previous Rental Details when the rental-step previous stay OR Minimum
 *   Living History extras exist (currently-renting / previously-renting, and
 *   first-time-renter for popup extras — Not Paying Rent situations only).
 *   Combined list first, then I Am Renting A Room / Not On The Lease when
 *   those answers are yes.
 * - first-time-renter / other / own-home are living-situation statuses: their
 *   address + living details live in the *FirstRenter keys (RentalStep stores
 *   them there for guarantors too), shown in ONE card titled by the status
 *   (e.g. "First-time Renter" / "Own Home"). That single card ALSO hosts the
 *   N4/L1 / new-to-country extras inline, the way Currently Renting /
 *   Previous Rental do. STATUS-gated (not bare presence) so stale
 *   cross-branch values from a status switch don't surface the wrong card —
 *   in sync with the react-pdf RentalSection. They are NOT "currently
 *   renting", so they never populate currentAddress / the Currently Renting
 *   card.
 * - Work Authorization Status / Expiration Date moved OUT of this section
 *   (QA 2026-09): the screening asks them on the Employment step now, so the
 *   Employment section renders them for every kind — old rental-step answers
 *   included — via resolveWorkAuthorization.
 * Status pill: USER-AUTHORED d815ac5a label strings via display.ts
 * rentalStatusLabel ('First-time Renter'); pill tones are a recorded
 * extrapolation. "Previous Rent" label corrects the design's copy slip
 * (parity §14 erratum 1). v1's SensitiveField PII masking is NOT ported
 * (unchanged recorded decision). Empty fields inside a shown card render
 * '-' per v2 house style (v1 hides them — recorded deviation).
 */
import {
	BriefcaseBusiness,
	CircleDollarSign,
	FolderOpenDot,
} from 'lucide-react'
import { FELONY_LABEL } from '#app/components/screening/student/RentalAdditionalQuestions'
import { deriveCustomQuestionRows } from '#app/utils/application-detail-v2/custom-question-display'
import {
	formatMoneyValue,
	optionLabel,
	questionLabel,
	rentalStatusLabel,
	yesNoLabel,
} from '#app/utils/application-detail-v2/display'
import { buildPreviousResidenceRows } from '#app/utils/application-detail-v2/rental-history-display'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import type { FormConfigV1 } from '#app/utils/application-form/config'
import {
	isLandlordContactFieldOn,
	isPrebuiltQuestionOn,
} from '#app/utils/application-form/form-gates'
import { cn } from '#app/utils/misc'
import { formatPhoneNumberWithFallback } from '#app/utils/phone-formatting'
import {
	DETAIL_CARD,
	FieldBox,
	IconSquare,
	PastelChip,
	type PastelTone,
	SectionCard,
} from '../primitives'
import { CustomQuestionAnswerList } from './CustomQuestionAnswerList'
import { PreviousRentalList } from './PreviousRentalList'

const PILL_TONES: Record<string, PastelTone> = {
	'currently-renting': 'green',
	'previously-renting': 'amber',
}

function OtherRentalFields({
	rental,
}: {
	rental: ParsedStepData['rental']
}) {
	return (
		<>
			{rental?.rentingRoom === 'yes' ? (
				<>
					<h3 className="col-span-2 text-[15px] font-semibold text-[#32302F]">
						I Am Renting A Room
					</h3>
					<FieldBox
						label="Room Rent / Month (CAD)"
						value={formatMoneyValue(rental.rentingRoomRent)}
					/>
					<FieldBox
						label="Rental Payment Method"
						value={optionLabel(rental.rentingRoomPaymentMethod)}
					/>
					<FieldBox label="Total Duration" value={rental.rentingRoomDuration} />
				</>
			) : null}
			{rental?.notOnLease === 'yes' ? (
				<>
					<h3 className="col-span-2 text-[15px] font-semibold text-[#32302F]">
						I Am Renting But I Am Not On The Lease
					</h3>
					<FieldBox
						label="Your Rent / Month (CAD)"
						value={formatMoneyValue(rental.notOnLeaseRent)}
					/>
					<FieldBox
						label="Rental Payment Method"
						value={optionLabel(rental.notOnLeasePaymentMethod)}
					/>
					<FieldBox label="Total Duration" value={rental.notOnLeaseDuration} />
				</>
			) : null}
		</>
	)
}

function YesNoWithExplanation({
	question,
	answer,
	explanation,
}: {
	question: string
	answer?: string
	explanation?: string
}) {
	return (
		<div className="space-y-4">
			<FieldBox label={questionLabel(question)} value={yesNoLabel(answer)} />
			{answer === 'yes' ? (
				<FieldBox label="Explanation">
					<span className="block text-[15px] font-medium whitespace-pre-wrap wrap-break-word text-[#32302F]">
						{explanation?.trim() || '-'}
					</span>
				</FieldBox>
			) : null}
		</div>
	)
}

export function RentalStatus({
	rental,
	formConfig,
	applicationType = 'primary',
	showStudentExtras,
}: {
	rental: ParsedStepData['rental']
	/** The application's frozen Application Form config (TT-65) — gates the
	 * landlord-contact fields, student prebuilt questions, and custom Q&A. */
	formConfig: FormConfigV1
	/** A guarantor isn't renting the unit — the section reads "Living Status"
	 * for them instead of "Rental Status". */
	applicationType?: 'primary' | 'co_applicant' | 'guarantor'
	/** Student housing omits N4/L1, rental work-auth, and new-to-country
	 * (not asked on the student form) and shows Additional Questions instead. */
	showStudentExtras?: boolean
}) {
	const { formatDate, formatShortDate } = useFormatDate()
	const status = rental?.rentalStatus?.trim() ?? ''
	const statusLabel = rentalStatusLabel(status)
	const pillTone: PastelTone = PILL_TONES[status] ?? 'neutral'

	// first-time-renter / other / own-home are living-situation statuses — their
	// address + work-auth live in the *FirstRenter keys (RentalStep stores them
	// there), NOT the currently-renting keys, so they render the living/work-auth
	// cards rather than "Currently Renting". Gate on the status, not bare
	// presence, so a value left behind when an applicant switches rental status
	// (e.g. first-time-renter → currently-renting) never surfaces the wrong
	// "(First Renter)" card. Mirrors the react-pdf IndividualReport's
	// `usesFirstRenter` gate — keeps the on-screen report and the PDF in sync
	// (documented deviation from v1's pure presence-driven render;
	// FULLSCREEN-DATA-MAP §11).
	const usesFirstRenter =
		status === 'first-time-renter' ||
		status === 'other' ||
		status === 'own-home'
	// Currently Renting / Previous Rental are RENTING cards — suppress them for a
	// living-situation status even if a stale currentAddress/previousAddress was
	// left behind by an earlier status (e.g. a guarantor who chose
	// currently-renting, then switched to own-home). The RentalStep submit only
	// writes the SELECTED status's keys and never clears the old ones, so the
	// detail page must status-gate (not bare presence) to stay in sync with the
	// living-details card and the react-pdf report.
	const hasCurrent = !usesFirstRenter && Boolean(rental?.currentAddress)
	const includeRentalStepPrevious =
		status === 'currently-renting' || status === 'previously-renting'
	const includePopupExtras =
		includeRentalStepPrevious || status === 'first-time-renter'
	const previousResidences = buildPreviousResidenceRows(rental, {
		includeRentalStepPrevious,
		includePopupExtras,
	})
	const showOtherRental =
		(includeRentalStepPrevious || status === 'first-time-renter') &&
		(rental?.rentingRoom === 'yes' || rental?.notOnLease === 'yes')
	const hasPrevious = previousResidences.length > 0 || showOtherRental
	// Living-situation statuses (own-home / other / first-time-renter) read their
	// address from the *FirstRenter key — but LEGACY guarantor data (stored
	// before RentalStep moved these statuses onto the *FirstRenter keys) kept
	// the PLAIN key (currentAddress). Resolve with a fallback so both layouts
	// render, preferring *FirstRenter — mirrors the read-fallback RentalStep
	// itself uses when loading the form.
	const firstRenterAddress =
		rental?.currentAddressFirstRenter || rental?.currentAddress
	// First-time-renter / "other" / own-home living details (address, living
	// situation, duration); shown in their own card so the details aren't lost
	// (TT2-504).
	const hasFirstRenterLiving =
		usesFirstRenter &&
		Boolean(
			firstRenterAddress ||
				rental?.currentLivingSituation ||
				rental?.yearsLivedAtAddress,
		)
	// Strictly 'yes' — v1:6429-6430; any other value renders nothing.
	const isNewToCountry = !showStudentExtras && rental?.newToCountry === 'yes'
	const hasEviction =
		!showStudentExtras && Boolean(rental?.receivedN4 || rental?.receivedL1)

	// Exactly ONE card hosts the cross-cutting extras (N4/L1, new-to-country).
	// Prefer the first-renter living card when that status is active, even if
	// Previous Rental Details also shows popup extras — keeps N4/L1 with the
	// living summary rather than buried under history rows.
	const host = hasCurrent
		? 'current'
		: usesFirstRenter
			? 'first-renter'
			: hasPrevious
				? 'previous'
				: 'standalone'

	const evictionBoxes = (
		<>
			<FieldBox
				label="Have You Received N4"
				value={yesNoLabel(rental?.receivedN4)}
			/>
			<FieldBox
				label="Have You Received L1"
				value={yesNoLabel(rental?.receivedL1)}
			/>
		</>
	)
	const newToCountryBoxes = isNewToCountry ? (
		<>
			<FieldBox label="New To Country" value="Yes" />
			<FieldBox
				label="Date Of Arrival"
				value={
					rental?.dateOfArrival ? formatShortDate(rental.dateOfArrival) : '-'
				}
			/>
		</>
	) : null
	/** Presence-gated extras (N4/L1, new-to-country): N4/L1 are no longer
	 * asked on the form — only legacy stored answers render. */
	const hostedExtras = (
		<>
			{hasEviction ? evictionBoxes : null}
			{newToCountryBoxes}
		</>
	)

	// Landlord contact fields are template-gated (TT-65): a box renders when
	// the frozen config enables the field OR the applicant actually answered it
	// (data sovereignty — configs can't hide collected answers). Legacy apps
	// resolve to the kind default (both fields on) → exactly today's layout.
	const showLandlordPhone = (value: string | undefined) =>
		Boolean(value?.trim()) || isLandlordContactFieldOn(formConfig, 'phone')
	const showLandlordEmail = (value: string | undefined) =>
		Boolean(value?.trim()) || isLandlordContactFieldOn(formConfig, 'email')

	const currentCard = hasCurrent ? (
		<SectionCard
			tone="rose"
			icon={<CircleDollarSign />}
			title="Currently Renting"
			className="rounded-2xl"
		>
			<FieldBox label="Landlord Name" value={rental?.currentLandlordName} />
			{showLandlordPhone(rental?.currentLandlordPhone) ? (
				<FieldBox
					label="Phone Number"
					value={formatPhoneNumberWithFallback(rental?.currentLandlordPhone)}
				/>
			) : null}
			{showLandlordEmail(rental?.currentLandlordEmail) ? (
				<FieldBox label="Email" value={rental?.currentLandlordEmail} />
			) : null}
			<FieldBox
				label="Current Rent (CAD)"
				value={formatMoneyValue(rental?.currentRent)}
			/>
			<FieldBox
				label="Rental Payment Method"
				value={optionLabel(rental?.currentPaymentMethod)}
			/>
			<FieldBox
				label="Total Duration Lived At Address"
				value={rental?.currentYearsLived}
			/>
			<FieldBox label="Address" value={rental?.currentAddress} />
			{showStudentExtras ? null : (
				<>
					{hasEviction ? evictionBoxes : null}
					{newToCountryBoxes}
				</>
			)}
		</SectionCard>
	) : null

	const previousCard = hasPrevious ? (
		<SectionCard
			tone="rose"
			icon={<CircleDollarSign />}
			title="Previous Rental Details"
			className="rounded-2xl"
		>
			<PreviousRentalList
				items={previousResidences}
				showLandlordPhone={showLandlordPhone}
				showLandlordEmail={showLandlordEmail}
				formatDate={formatDate}
			/>
			{showOtherRental ? (
				<div className="col-span-2 grid grid-cols-2 gap-4">
					<OtherRentalFields rental={rental} />
				</div>
			) : null}
			{host === 'previous' ? hostedExtras : null}
		</SectionCard>
	) : null

	const firstRenterCard =
		hasFirstRenterLiving ||
		(usesFirstRenter && (hasEviction || isNewToCountry)) ? (
			<SectionCard
				tone="rose"
				icon={<CircleDollarSign />}
				// Self-label the card with the status (e.g. "First-time Renter"), the
				// way "Currently Renting" / "Previous Rental" do — the status pill that
				// used to carry it is commented out, so a generic "Living Details" left
				// the first-time-renter state unlabeled. Mirrors standaloneCard.
				title={statusLabel ?? 'Living Details'}
				className="rounded-2xl"
			>
				{hasFirstRenterLiving ? (
					<>
						<FieldBox
							label="Current Address"
							value={firstRenterAddress}
							fullWidth
						/>
						{status !== 'own-home' ? (
							<FieldBox
								label="Current Living Situation"
								value={rental?.currentLivingSituation}
							/>
						) : null}
						<FieldBox
							label="Total Duration Lived At Address"
							value={rental?.yearsLivedAtAddress}
						/>
					</>
				) : null}
				{host === 'first-renter' ? (
					<>
						{hasEviction ? evictionBoxes : null}
						{newToCountryBoxes}
					</>
				) : null}
			</SectionCard>
		) : null

	const standaloneCard =
		!usesFirstRenter &&
		host === 'standalone' &&
		(hasEviction || isNewToCountry) ? (
			<SectionCard
				tone="rose"
				icon={<CircleDollarSign />}
				title={statusLabel ?? 'Rental Details'}
				className="rounded-2xl"
			>
				{hostedExtras}
			</SectionCard>
		) : null

	const hasCards = Boolean(
		currentCard || previousCard || firstRenterCard || standaloneCard,
	)

	const everEvicted = rental?.everEvicted
	const everConvictedFelony = rental?.everConvictedFelony
	const doesSmoke = rental?.doesSmoke
	// Prebuilt student questions are template-gated (TT-65): a row renders when
	// the frozen config keeps the question on OR an answer exists (answers can't
	// be hidden by a later template change — the config is pinned anyway).
	// Legacy student apps resolve to the student default (all on) → unchanged.
	const showEverEvicted =
		Boolean(showStudentExtras) &&
		(isPrebuiltQuestionOn(formConfig, 'rental_questions', 'ever_evicted') ||
			Boolean(everEvicted))
	const showFelony =
		Boolean(showStudentExtras) &&
		(isPrebuiltQuestionOn(
			formConfig,
			'rental_questions',
			'felony_conviction',
		) ||
			Boolean(everConvictedFelony))
	const showSmoke =
		Boolean(showStudentExtras) &&
		(isPrebuiltQuestionOn(formConfig, 'rental_questions', 'do_you_smoke') ||
			Boolean(doesSmoke))
	// Custom questions render for EVERY kind/type that answered them — the
	// standard flow now collects them too (dynamic Application Form, TT-65).
	const customRows = deriveCustomQuestionRows(
		formConfig,
		'rental_questions',
		rental?.customQuestionAnswers,
	)
	const showAdditionalQuestions =
		showEverEvicted || showFelony || showSmoke || customRows.length > 0

	const additionalQuestionsCard = showAdditionalQuestions ? (
		<SectionCard
			tone="green"
			icon={<BriefcaseBusiness />}
			title="Additional Questions"
			className="rounded-2xl"
			columns={1}
		>
			{showEverEvicted ? (
				<YesNoWithExplanation
					question="Have You Ever Been Evicted?"
					answer={everEvicted}
					explanation={rental?.everEvictedExplanation}
				/>
			) : null}
			{showFelony ? (
				<YesNoWithExplanation
					question={FELONY_LABEL}
					answer={everConvictedFelony}
					explanation={rental?.everConvictedFelonyExplanation}
				/>
			) : null}
			{showSmoke ? (
				<FieldBox
					label={questionLabel('Do You Smoke?')}
					value={yesNoLabel(doesSmoke)}
				/>
			) : null}
			<CustomQuestionAnswerList rows={customRows} />
		</SectionCard>
	) : null

	// Figma: left column stacks Currently Renting (or living card) + Additional
	// Questions; right column is Previous Rental Details beside both.
	const leftColumn =
		currentCard ||
		firstRenterCard ||
		standaloneCard ||
		additionalQuestionsCard ? (
			<div className="flex flex-col gap-6">
				{currentCard}
				{firstRenterCard}
				{standaloneCard}
				{additionalQuestionsCard}
			</div>
		) : null

	return hasCards || showAdditionalQuestions ? (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex flex-wrap items-center gap-3">
				<IconSquare tone="green">
					<FolderOpenDot />
				</IconSquare>
				<h2 className="text-[19px] font-semibold text-[#32302F]">
					{applicationType === 'guarantor' ? 'Living Status' : 'Rental Status'}
				</h2>
				{/* {statusLabel ? (
					<PastelChip tone={pillTone} label={statusLabel} />
				) : null} */}
			</div>

			<div className="mt-5 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
				{leftColumn}
				{previousCard}
			</div>
		</section>
	) : null
}
